import {
  Modal,
  Box,
  Typography,
  Button,
  Radio,
  Stack,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import ProvisionalOfferOption from "../../assets/icons/provisional_offer_option.png";
import CoApplicantOption from "../../assets/icons/coapplicant_option.png";
import ToggleWhite from "../../assets/icons/toggle-white.png";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { useDispatch, useSelector } from "react-redux";
import {
  setActiveHorizontalIndex,
  setActiveVerticalIndex,
  setBreData,
  setCoAppAddOption,
  setIsEligible,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { RootState } from "../../services/redux/store";
import { trackCustomerJourneyEventAndWait } from "../../utils/ga";
import { useMemo } from "react";
import { decrypt } from "../../utils/crypto";
import { getGAProvisionalOfferStatements } from "./StepperDyanmicForm/helper";

export type ContinueOption = "provisional" | "co-applicant";

interface OptionConfig {
  id: ContinueOption;
  title: string;
  description: string;
  icon: string;
}

const OPTIONS: OptionConfig[] = [
  {
    id: "provisional",
    title: "Proceed with Provisional Offer",
    description:
      "Continue instantly with your current eligibility and view your provisional loan offer.",
    icon: ProvisionalOfferOption,
  },
  {
    id: "co-applicant",
    title: "Continue by Adding Co-Applicant Details",
    description:
      "Improve your eligibility by including a co-applicant in the application process.",
    icon: CoApplicantOption,
  },
];

interface ContinueOptionsModalProps {
  open: boolean;
  onClose: () => void;
  onProceed: (option: ContinueOption) => void;
  selectedVal: ContinueOption | null;
  setSelectedVal: (val: ContinueOption) => void;
  setSnackbar: (val: any) => void;
  applicationSource: string;
  userProfile: any;
}

export function SkipCoApplicantOption({
  open,
  onClose,
  onProceed,
  selectedVal,
  setSelectedVal,
  setSnackbar,
  applicationSource,
  userProfile,
}: ContinueOptionsModalProps) {
  const dispatch = useDispatch();

  const trackingId = useSelector(
    (state: RootState) => state.stepper,
  ).trackingId;

   const handleGAProvisionalOfferStatments = useMemo(
      () => getGAProvisionalOfferStatements(applicationSource),
      [applicationSource]
    );

  const onProceedClick = () => {
    if (selectedVal === "co-applicant") {
      dispatch(setCoAppAddOption("co-applicant"));
      onProceed("co-applicant");
    } else {
      trackCustomerJourneyEventAndWait(
        `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE9_SKIPCOAPPLICANT`,
        decrypt(userProfile?.id) ?? "",
        trackingId,
        {
          label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE9_SKIPCOAPPLICANT`,
          category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE9_SKIPCOAPPLICANT`,
          action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE9_SKIPCOAPPLICANT`,
          page: "Provisional_Offer Page9_Skip_Coapplicant",
        }
      );
      dispatch(setCoAppAddOption("provisional"));
      onProceed("provisional");
    }
  };

  const handleBreInvokApi = async () => {
    try {
      const payload = {
        applicationId: trackingId,
        name: "getCustomerEligibility",
      };
      const res: any = await eligibilityCheckServices.fetchBreApi(payload);
      if (res?.status === 400) {
        setSnackbar({
          open: true,
          message:
            "Uh-oh! We are experiencing some technical issues. Please try again later.",
          severity: "error",
        });
        return;
      }

      if (res?.data?.data?.Scheme_Available != "Yes") {
        dispatch(setIsEligible(false));
        return;
      }
      dispatch(setActiveHorizontalIndex(2));
      dispatch(setActiveVerticalIndex(0));
      dispatch(setBreData(res?.data?.data));
      dispatch(setIsEligible(res?.data?.data?.Scheme_Available == "Yes"));
    } catch (e) {
      return;
    }
  };
  return (
    <Modal open={open} onClose={onClose} aria-labelledby="continue-modal-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92%", sm: 660 },
          bgcolor: "#E8F6F4",
          borderRadius: 2,
          boxShadow: 24,
          p: { xs: 2.5, sm: 3.5 },
          outline: "none",
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "grey.600" }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          id="continue-modal-title"
          variant="body2"
          sx={{ fontWeight: 700, color: "#0A0A0A", pr: 4, fontSize: "2rem" }}
        >
          Choose How You'd Like to Continue
        </Typography>
        <Typography sx={{ mt: 1, color: "grey.600", fontSize: 16 }}>
          You are eligible to move ahead with your loan journey. Select one of
          the following options to continue.
        </Typography>

        <Stack spacing={1.5} sx={{ mt: 2.5 }}>
          {OPTIONS.map((opt) => {
            const isSelected = selectedVal === opt.id;
            return (
              <Box
                key={opt.id}
                onClick={() => setSelectedVal(opt.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedVal(opt.id);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 2,
                  bgcolor: "#fff",
                  borderRadius: 1.5,
                  cursor: "pointer",
                  border: "2px solid",
                  borderColor: isSelected ? "#1AA89A" : "transparent",
                  transition: "border-color 0.2s",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={opt.icon}
                    alt={`${opt.id}-icon`}
                    style={{ width: 40, height: 40 }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: "#0A0A0A",
                    }}
                  >
                    {opt.title}
                  </Typography>
                  <Typography sx={{ fontSize: 15, color: "grey.600", mt: 0.5 }}>
                    {opt.description}
                  </Typography>
                </Box>
                <Radio
                  checked={isSelected}
                  value={opt.id}
                  sx={{
                    p: 0,
                    color: "grey.400",
                    "&.Mui-checked": { color: "#1AA89A" },
                  }}
                  checkedIcon={
                    <img
                      src={ToggleWhite}
                      alt="selected"
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                      }}
                    />
                  }
                />
              </Box>
            );
          })}
        </Stack>

        <Button
          fullWidth
          onClick={onProceedClick}
          disabled={selectedVal === null}
          sx={{
            mt: 3,
            py: 1.5,
            bgcolor: "#1AA89A",
            color: "#fff",
            fontWeight: 600,
            textTransform: "none",
            fontSize: 16,
            "&:hover": { bgcolor: "#158F83" },
          }}
        >
          Proceed
        </Button>
      </Box>
    </Modal>
  );
}
